import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import cl from './Login.module.css';
import img1 from './img/1.gif';
import UserService from '../../API/UserService';
import { useLoader } from '../../hooks/useLoader';
import { useAuth } from '../../context/AuthContext';
import YandexLoginButton from '../../components/OAuth/YandexLoginButton';
import GoogleLoginButton from '../../components/OAuth/GoogleLoginButton';
import TripleCaptcha from '../../components/UI/TripleCaptcha/TripleCaptcha';

const Login = () => {
  const [activeTab, setActiveTab] = useState('login');
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ 
    username: '', 
    email: '', 
    password: '', 
    confirmPassword: '' 
  });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  // 2026-05-29: adaptive-капча включается на бэке при velocity-burst.
  // Чек делается при загрузке Login и перед каждой регистрацией.
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const captchaRef = useRef(null);

  const navigate = useNavigate();
  const { login: authLogin } = useAuth();

  // Проверяем нужна ли капча на регистрацию
  useEffect(() => {
    UserService.isCaptchaRequired().then(setCaptchaRequired);
  }, []);

  // Валидация email
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  // Валидация логина
  const validateUsername = (username) => {
    return username.length >= 4 && !/^\d/.test(username);
  };

  // Функция для обработки ошибок с сервера
  const handleServerError = (errorMessage) => {
    setServerError(errorMessage);
    setTimeout(() => setServerError(''), 5000); // Убираем ошибку через 5 секунд
  };

  // Функция для обработки успеха
  const handleSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000); // Убираем сообщение через 3 секунды
  };

  // Обработка входа с использованием useLoader
  const [loginFetch, isLoginLoading, loginError] = useLoader(async () => {
    const newErrors = {};

    if (!loginData.email) newErrors.email = 'Email обязателен';
    else if (!validateEmail(loginData.email)) newErrors.email = 'Некорректный email';
    
    if (!loginData.password) newErrors.password = 'Пароль обязателен';
    else if (loginData.password.length < 8) newErrors.password = 'Пароль минимум 8 символов';

    setErrors(newErrors);
    
    if (Object.keys(newErrors).length === 0) {
      try {
        // Используем промис-обертку для UserService.login
        await new Promise((resolve, reject) => {
          UserService.login(
            loginData,
            () => {
              // Success callback
              resolve();
            },
            (error) => {
              // Error callback
              reject(new Error(error));
            }
          );
        });

        // Получаем токен и авторизуемся через контекст
        const token = localStorage.getItem('Authorization');
        if (token) {
          authLogin(token);
          handleSuccess('Успешный вход в систему!');
          // Переход в Main компонент
          setTimeout(() => {
            navigate('/main');
          }, 1500);
        }
      } catch (error) {
        handleServerError(error.message);
      }
    } else {
      throw new Error('Проверьте введенные данные');
    }
  });

  // Обработка регистрации с использованием useLoader
  const [registerFetch, isRegisterLoading, registerError] = useLoader(async () => {
    const newErrors = {};

    if (!registerData.username) newErrors.username = 'Логин обязателен';
    else if (!validateUsername(registerData.username)) 
      newErrors.username = 'Логин минимум 4 символа, не начинается с цифры';
    
    if (!registerData.email) newErrors.email = 'Email обязателен';
    else if (!validateEmail(registerData.email)) newErrors.email = 'Некорректный email';
    
    if (!registerData.password) newErrors.password = 'Пароль обязателен';
    else if (registerData.password.length < 8) newErrors.password = 'Пароль минимум 8 символов';
    
    if (!registerData.confirmPassword) newErrors.confirmPassword = 'Повторите пароль';
    else if (registerData.password !== registerData.confirmPassword) 
      newErrors.confirmPassword = 'Пароли не совпадают';

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      // Если адаптивная капча активна — собираем три ответа и шлём вместе.
      // Бэк (signup_guard.py) проверит и при ошибке вернёт 403 captcha_required.
      let captchaPayload = {};
      if (captchaRequired) {
        if (!captchaRef.current || !captchaRef.current.isReady()) {
          handleServerError('Заполните капчу: введите 2 кода и нарисуйте кота');
          throw new Error('captcha incomplete');
        }
        captchaPayload = captchaRef.current.collect();
      }

      const payload = {
        firstName: registerData.username,
        email: registerData.email,
        password: registerData.password,
        ...captchaPayload,
      };
      const res = await UserService.signupWithCaptcha(payload);

      if (res.status === 201 || res.data?.message === 'Пользователь создан') {
        handleSuccess('Регистрация прошла успешно! Теперь войдите в систему.');
        setTimeout(() => {
          setActiveTab('login');
          setRegisterData({ username: '', email: '', password: '', confirmPassword: '' });
        }, 2000);
      } else if (res.data?.captcha_required) {
        // Бэк включил капчу прямо сейчас (velocity-burst после старта формы).
        setCaptchaRequired(true);
        if (captchaRef.current) captchaRef.current.refresh();
        handleServerError('Заполните капчу — обновили картинки');
        throw new Error('captcha now required');
      } else {
        const msg = res.data?.message || 'Регистрация не удалась';
        handleServerError(msg);
        throw new Error(msg);
      }
    } else {
      throw new Error('Проверьте введенные данные');
    }
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setServerError('');
    setSuccessMessage('');
    await loginFetch();
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setServerError('');
    setSuccessMessage('');
    await registerFetch();
  };

  const handleLoginChange = (e) => {
    setLoginData({ ...loginData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  const handleRegisterChange = (e) => {
    setRegisterData({ ...registerData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  return (
    <div className={cl.mainwrap}>
      <div className={cl.wrapForm}>
        {/* Вкладки */}
        <div className={cl.tabs}>
          <button 
            className={`${cl.tab} ${activeTab === 'login' ? cl.activeTab : ''}`}
            onClick={() => {
              setActiveTab('login');
              setErrors({});
              setServerError('');
              setSuccessMessage('');
            }}
          >
            Вход
          </button>
          <button 
            className={`${cl.tab} ${activeTab === 'register' ? cl.activeTab : ''}`}
            onClick={() => {
              setActiveTab('register');
              setErrors({});
              setServerError('');
              setSuccessMessage('');
            }}
          >
            Регистрация
          </button>
        </div>

        <div className={cl.formContainer}>
          <div className={cl.title}>
            <h2>{activeTab === 'login' ? 'Добро пожаловать!' : 'Создать аккаунт'}</h2>
            <p>{activeTab === 'login' ? 'Войдите в свой аккаунт' : 'Присоединяйтесь к нам'}</p>
          </div>

          {/* Отображение сообщений */}
          {serverError && (
            <div className={cl.serverError}>
              {serverError}
            </div>
          )}

          {successMessage && (
            <div className={cl.successMessage}>
              {successMessage}
            </div>
          )}

          {/* OAuth-кнопки (один источник входа для обоих табов).
              Yandex/Google авторизация делает то же что и регистрация:
              если юзера нет — создаёт, есть — логинит. */}
          <div style={{ marginBottom: 16 }}>
            <YandexLoginButton disabled={isLoginLoading || isRegisterLoading} />
            <GoogleLoginButton disabled={isLoginLoading || isRegisterLoading} />
            <div style={{
              textAlign: 'center',
              margin: '14px 0 4px',
              color: '#94a3b8',
              fontSize: 13,
              position: 'relative',
            }}>
              <span style={{ background: '#10131a', padding: '0 12px', position: 'relative', zIndex: 1 }}>
                или {activeTab === 'login' ? 'войти по email' : 'зарегистрироваться по email'}
              </span>
              <div style={{
                position: 'absolute',
                top: '50%',
                left: 0, right: 0,
                height: 1,
                background: '#1f2937',
                zIndex: 0,
              }} />
            </div>
          </div>

          {/* Форма входа */}
          {activeTab === 'login' && (
            <form onSubmit={handleLogin} className={cl.form}>
              <div className={cl.inputGroup}>
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={loginData.email}
                  onChange={handleLoginChange}
                  className={`${cl.input} ${errors.email ? cl.error : ''}`}
                  disabled={isLoginLoading}
                />
                {errors.email && <span className={cl.errorText}>{errors.email}</span>}
              </div>

              <div className={cl.inputGroup}>
                <input
                  type="password"
                  name="password"
                  placeholder="Пароль"
                  value={loginData.password}
                  onChange={handleLoginChange}
                  className={`${cl.input} ${errors.password ? cl.error : ''}`}
                  disabled={isLoginLoading}
                />
                {errors.password && <span className={cl.errorText}>{errors.password}</span>}
              </div>

              <button 
                type="submit" 
                className={`${cl.submitBtn} ${isLoginLoading ? cl.loading : ''}`}
                disabled={isLoginLoading}
              >
                {isLoginLoading ? 'Вход...' : 'Войти'}
              </button>
            </form>
          )}

          {/* Форма регистрации */}
          {activeTab === 'register' && (
            <form onSubmit={handleRegister} className={cl.form}>
              <div className={cl.inputGroup}>
                <input
                  type="text"
                  name="username"
                  placeholder="Логин"
                  value={registerData.username}
                  onChange={handleRegisterChange}
                  className={`${cl.input} ${errors.username ? cl.error : ''}`}
                  disabled={isRegisterLoading}
                />
                {errors.username && <span className={cl.errorText}>{errors.username}</span>}
              </div>

              <div className={cl.inputGroup}>
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={registerData.email}
                  onChange={handleRegisterChange}
                  className={`${cl.input} ${errors.email ? cl.error : ''}`}
                  disabled={isRegisterLoading}
                />
                {errors.email && <span className={cl.errorText}>{errors.email}</span>}
              </div>

              <div className={cl.inputGroup}>
                <input
                  type="password"
                  name="password"
                  placeholder="Пароль"
                  value={registerData.password}
                  onChange={handleRegisterChange}
                  className={`${cl.input} ${errors.password ? cl.error : ''}`}
                  disabled={isRegisterLoading}
                />
                {errors.password && <span className={cl.errorText}>{errors.password}</span>}
              </div>

              <div className={cl.inputGroup}>
                <input
                  type="password"
                  name="confirmPassword"
                  placeholder="Повторить пароль"
                  value={registerData.confirmPassword}
                  onChange={handleRegisterChange}
                  className={`${cl.input} ${errors.confirmPassword ? cl.error : ''}`}
                  disabled={isRegisterLoading}
                />
                {errors.confirmPassword && <span className={cl.errorText}>{errors.confirmPassword}</span>}
              </div>

              {/* Adaptive-капча: показываем только когда сервер просит. */}
              {captchaRequired && (
                <TripleCaptcha ref={captchaRef} disabled={isRegisterLoading} />
              )}

              <button
                type="submit"
                className={`${cl.submitBtn} ${isRegisterLoading ? cl.loading : ''}`}
                disabled={isRegisterLoading}
              >
                {isRegisterLoading ? 'Регистрация...' : 'Зарегистрироваться'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;